import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';
import { useAuth } from '../context/AuthContext';
import { listShipments } from '../services/shipmentService';

export const useShipments = () => {
  const [shipments, setShipments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, isAuthenticated } = useAuth();
  const debounceTimerRef = useRef(null);

  const fetchShipments = useCallback(async (isInitial = true) => {
    if (!user?.id) {
      setShipments([]);
      setIsLoading(false);
      return;
    }

    try {
      if (isInitial) setIsLoading(true);
      setError(null);

      const data = await listShipments();
      setShipments(data || []);
    } catch (err) {
      console.error('Error fetching shipments:', err);
      setError(err.message || 'Failed to fetch shipments');
    } finally {
      if (isInitial) setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setShipments([]);
      setIsLoading(false);
      return undefined;
    }

    fetchShipments(true);

    const debouncedUpdate = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => fetchShipments(false), 800);
    };

    const channel = supabase
      .channel(`shipments-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shipments' },
        debouncedUpdate
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'shipment_authorizations' },
        debouncedUpdate
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [isAuthenticated, user?.id, fetchShipments]);

  return {
    shipments,
    isLoading,
    error,
    refresh: () => fetchShipments(false),
  };
};
